import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, MapPin, ChevronDown, 
  ChevronUp, ExternalLink, Save, Printer, ShoppingBag
} from 'lucide-react';
import api from "../../../../api/axios";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingAddress, setEditingAddress] = useState({});
  const [currentFilter, setCurrentFilter] = useState("all");

  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;
  const LOGO_PATH = '/assets/images/logo.png'; 

  const fetchOrders = async () => {
    try {
      const res = await api.get("/orders");
      if (res.data.success) {
        setOrders(res.data.data);
        const addrMap = {};
        res.data.data.forEach(o => addrMap[o.id] = o.address || "");
        setEditingAddress(addrMap);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  // --- LOGIC ---

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await api.post(`/orders/${id}/status`, { status: newStatus });
      if (res.data.success) {
        setOrders(orders.map(o => o.id === id ? { ...o, status: newStatus } : o));
      }
    } catch (err) { alert("Erreur lors du changement de statut"); }
  };

  const handleSaveAddress = async (id) => {
    try {
      const res = await api.post(`/orders/${id}/address`, { address: editingAddress[id] });
      if (res.data.success) {
        setOrders(orders.map(o => o.id === id ? { ...o, address: editingAddress[id] } : o));
        alert("Adresse mise à jour !");
      }
    } catch (err) { alert("Erreur d'enregistrement"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette commande définitivement ?")) return;
    try {
      const res = await api.delete(`/orders/${id}`);
      if (res.data.success) {
        setOrders(orders.filter(order => order.id !== id));
      }
    } catch (err) { alert("Erreur lors de la suppression."); }
  };

  // --- PRO FILTERING LOGIC ---
  const filteredOrders = useMemo(() => {
    if (currentFilter === "all") return orders;
    return orders.filter(o => o.status === currentFilter);
  }, [currentFilter, orders]);

  const filterTabs = [
    { id: 'all', label: 'Toutes', count: orders.length },
    { id: 'pending', label: 'En attente', count: orders.filter(o => o.status === 'pending').length },
    { id: 'processing', label: 'Traitement', count: orders.filter(o => o.status === 'processing').length },
    { id: 'shipped', label: 'Expédié', count: orders.filter(o => o.status === 'shipped').length },
    { id: 'delivered', label: 'Livré', count: orders.filter(o => o.status === 'delivered').length },
    { id: 'cancelled', label: 'Annulé', count: orders.filter(o => o.status === 'cancelled').length },
  ];

  const getStatusStyle = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-600';
      case 'cancelled': return 'bg-red-100 text-red-600';
      case 'shipped': return 'bg-blue-100 text-blue-600';
      case 'processing': return 'bg-purple-100 text-purple-600';
      default: return 'bg-orange-100 text-orange-600';
    }
  };

  // --- PRINT LOGIC ---
  const handlePrint = (order) => {
    const printWindow = window.open('', '_blank');
    const productRows = order.items.map(item => `
      <tr>
        <td style="padding: 12px; border-bottom: 1px solid #eee; font-size: 12px;">${item.product?.title}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right;">${parseFloat(item.price_at_sale).toFixed(2)} DH</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right; font-weight: bold;">${(item.quantity * item.price_at_sale).toFixed(2)} DH</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Facture #${order.order_number}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #1a1a1a; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 5px solid #FFAA00; padding-bottom: 20px; }
            .logo { height: 50px; }
            .info-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 30px; margin-top: 30px; }
            .info-box { padding: 20px; background: #fcfcfc; border: 1px solid #eee; border-radius: 15px; }
            .info-box h3 { margin: 0 0 10px 0; font-size: 11px; color: #FFAA00; text-transform: uppercase; letter-spacing: 1px; }
            table { width: 100%; border-collapse: collapse; margin-top: 30px; }
            th { background: #1A1A1A; color: white; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
            .total-section { margin-top: 30px; text-align: right; border-top: 2px solid #FFAA00; padding-top: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${LOGO_PATH}" class="logo" />
            <div style="text-align: right">
              <h1 style="margin:0; font-size: 24px;">BON DE COMMANDE</h1>
              <p style="margin:0; font-weight: bold;">#${order.order_number}</p>
              <p style="margin:0; font-size: 12px; color: #888;">${new Date(order.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div class="info-grid">
            <div class="info-box">
              <h3>Client</h3>
              <strong>${order.full_name}</strong><br/>
              ${order.phone}<br/>
              ${order.email ? `<span style="font-size: 12px; color: #555;">${order.email}</span>` : ''}
            </div>
            <div class="info-box">
              <h3>Livraison</h3>
              <strong>${order.city}</strong> 
              ${order.mode_reception ? ` - <span style="font-size:11px; color:#FFAA00;">${order.mode_reception}</span>` : ''}<br/>
              ${order.address || 'Livraison à domicile'}<br/>
              ${order.quartier ? `Quartier: ${order.quartier}<br/>` : ''}
              ${order.commentaire ? `<br/><em style="font-size: 12px; color: #666;">Note: ${order.commentaire}</em>` : ''}
            </div>
          </div>
          <table>
            <thead>
              <tr><th>Produit</th><th style="text-align: center">Qté</th><th style="text-align: right">Prix Unitaire</th><th style="text-align: right">Total</th></tr>
            </thead>
            <tbody>${productRows}</tbody>
          </table>
          <div class="total-section">
            <p style="margin:0; font-size: 14px; color: #888;">Total Net à Payer</p>
            <h2 style="margin:0; color: #FFAA00; font-size: 28px;">${parseFloat(order.total_amount).toFixed(2)} DH</h2>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-4 py-8">
      
      {/* Header & Pro Tabs */}
      <div className="flex flex-col xl:flex-row xl:items-end justify-between mb-8 gap-6">
        <div>
          <h2 className="text-3xl font-black uppercase tracking-tighter text-gray-900">Commandes Clients</h2>
          <p className="text-sm font-bold text-[#8A8A89]">Gestion logistique & Facturation</p>
        </div>

        <div style={{display:"none"}}>{loading}</div>

        {/* Tab Filter System */}
        <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 overflow-x-auto no-scrollbar gap-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentFilter(tab.id)}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap ${
                currentFilter === tab.id 
                ? 'bg-black text-white shadow-lg' 
                : 'text-gray-400 hover:text-black hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-md text-[9px] ${
                currentFilter === tab.id ? 'bg-[#ffaa00] text-black' : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-black uppercase text-[#8A8A89] tracking-widest">
                <th className="px-6 py-6 text-center w-16">ID</th>
                <th className="px-6 py-6">Client</th>
                <th className="px-6 py-6">Informations de Livraison</th>
                <th className="px-6 py-6">Total</th>
                <th className="px-6 py-6">Statut</th>
                <th className="px-6 py-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <AnimatePresence mode='popLayout'>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <motion.tr 
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        className="hover:bg-gray-50/30 transition-colors group"
                      >
                        <td className="px-6 py-5 text-center align-top">
                          <span className="text-[10px] font-black text-gray-300">#{order.id}</span>
                        </td>
                        <td className="px-6 py-5 align-top">
                          <div className="flex flex-col">
                            <span className="font-black text-gray-900 text-sm">{order.full_name}</span>
                            <span className="text-[11px] font-bold text-[#8A8A89]">{order.phone}</span>
                            {order.email && (
                              <span className="text-[10px] text-gray-400 mt-1">{order.email}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5 align-top">
                          <div className="flex flex-col gap-2 max-w-[280px]">
                            
                            {/* City & Mode Badge */}
                            <div className="flex items-center gap-2">
                              <span className="flex items-center gap-1 text-[10px] font-black text-[#ffaa00] uppercase">
                                <MapPin size={10} /> {order.city}
                              </span>
                              {order.mode_reception && (
                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${
                                  order.mode_reception.includes('magasin') 
                                    ? 'bg-purple-100 text-purple-600' 
                                    : 'bg-blue-100 text-blue-600'
                                }`}>
                                  {order.mode_reception}
                                </span>
                              )}
                            </div>

                            {/* Address Input */}
                            <div className="flex gap-2">
                              <input 
                                type="text" value={editingAddress[order.id] || ""}
                                onChange={(e) => setEditingAddress({...editingAddress, [order.id]: e.target.value})}
                                placeholder="Adresse..."
                                className="w-full bg-gray-50 border border-gray-100 px-3 py-2 rounded-xl text-[11px] font-bold outline-none focus:border-[#ffaa00] focus:bg-white transition-all"
                              />
                              <button onClick={() => handleSaveAddress(order.id)} className="p-2 bg-black text-white rounded-xl hover:bg-[#ffaa00] transition-colors shadow-sm">
                                <Save size={14} />
                              </button>
                            </div>

                            {/* Quartier & Commentaire */}
                            <div className="flex flex-col gap-0.5 mt-1">
                              {order.quartier && (
                                <span className="text-[10px] text-gray-500 font-medium">
                                  <strong className="uppercase tracking-widest text-[9px]">Quartier:</strong> {order.quartier}
                                </span>
                              )}
                              {order.commentaire && (
                                <span className="text-[10px] text-gray-400 italic break-words mt-1">
                                  "{order.commentaire}"
                                </span>
                              )}
                            </div>

                          </div>
                        </td>
                        <td className="px-6 py-5 font-black text-[#ffaa00] whitespace-nowrap align-top">
                          {parseFloat(order.total_amount).toFixed(2)} DH
                        </td>
                        <td className="px-6 py-5 align-top">
                          <select 
                            value={order.status} 
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            className={`text-[10px] font-black uppercase px-3 py-2 rounded-xl outline-none cursor-pointer transition-all border-none ${getStatusStyle(order.status)}`}
                          >
                            <option value="pending">En attente</option>
                            <option value="processing">Traitement</option>
                            <option value="shipped">Expédié</option>
                            <option value="delivered">Livré</option>
                            <option value="cancelled">Annulé</option>
                          </select>
                        </td>
                        <td className="px-6 py-5 text-right align-top">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handlePrint(order)} className="p-2.5 bg-blue-50 text-blue-500 rounded-xl hover:bg-blue-500 hover:text-white transition-all">
                              <Printer size={18} />
                            </button>
                            <button onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)} className="p-2.5 bg-gray-100 text-gray-400 rounded-xl hover:bg-black hover:text-white transition-all">
                              {expandedOrder === order.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                            <button onClick={() => handleDelete(order.id)} className="p-2.5 bg-red-50 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </motion.tr>

                      {/* Expanded Order Items */}
                      <AnimatePresence>
                        {expandedOrder === order.id && (
                          <motion.tr 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <td colSpan="6" className="px-8 py-6 bg-gray-50/50">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {order.items.map((item) => (
                                  <div key={item.id} className="bg-white p-4 rounded-2xl border border-gray-100 flex gap-4 items-center shadow-sm">
                                    <div className="w-16 h-16 bg-white rounded-xl overflow-hidden shrink-0 border border-gray-100">
                                      <img src={`${BACKEND_BASE}/storage/${item.product?.main_image}`} className="w-full h-full object-contain" alt="" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-bold text-[11px] text-gray-800 truncate uppercase">{item.product?.title}</h4>
                                      <div className="flex justify-between items-center mt-1">
                                        <span className="text-[10px] font-black text-[#ffaa00]"><span className='text-[13px]'>({item.quantity})</span> x {item.price_at_sale} DH</span>
                                        <a href={`/product/${item.product?.slug}`} target="_blank" rel="noreferrer" className="text-gray-300 hover:text-[#ffaa00] transition-colors">
                                          <ExternalLink size={14} />
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-32 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center">
                          <ShoppingBag className="text-gray-200" size={32} />
                        </div>
                        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                          Aucune commande "{currentFilter}" trouvée
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default Orders;